import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';

import {ActionMenu} from '../action-menu/action-menu';
import type {SelectMenuOption} from '../action-menu/action-menu';
import {EmailLinkedEntities} from '../email-address-links/email-linked-entities';
import {normalizeEmailAddresseeEntity} from '../email-address-links/helpers';
// @ts-expect-error - imported from .js/.jsx file
import {CampaignIcon} from '../icon/svg/campaign-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from '../icon/svg/eye-icon';
// @ts-expect-error - imported from .js/.jsx file
import {HtmlAttachmentIcon} from '../icon/svg/html-attachment-icon';
import {StatusBadge} from '../status-badge/status-badge';
import {FormattedTimestamp} from '../timestamp/formatted-timestamp';

import type {SequenceInfo, EmailListItem, EmailStatus} from './types';

import styles from './email-row-item.module.css';

type Props = {
    sequenceInfo?: SequenceInfo | null | undefined;
    email: EmailListItem;
    getRef: (arg1?: HTMLElement | null | undefined) => void;
    status?: EmailStatus;
    actions: SelectMenuOption[];
    onExpandRow: () => any;
    deletingDialog: React.ReactNode | null | undefined;
    navToSequenceDetails?: () => void;
    // Can't use CSS psuedo selectors here (without some hacks)
    // so we'll pass in a prop to toggle our top-border if this
    // row is the first of its date.
    isFirstOfDate: boolean;
    // This should be true if the row item is an incoming email, rather than
    // outgoing. This switches around the to and from components
    isReceivedEmail: boolean;
};

export class EmailRowItemCollapsed extends React.PureComponent<Props> {
    actionMenu: HTMLElement | null | undefined;

    componentDidMount() {
        ReactTooltip.rebuild();
    }

    render() {
        const {email, status, sequenceInfo} = this.props;
        const styleNames = classnames({
            [styles['email-row-item--collapsed']]: true,
            [styles['email-row-item--hidden']]:
                this.props.status && this.props.status.level === 'hidden',
            [styles['email-row-item--first-of-date']]: this.props.isFirstOfDate,
        });

        return (
            <div onClick={this.props.onExpandRow} ref={this.props.getRef} className={styleNames}>
                {this.renderHeaderLeft(email, status)}
                {this.renderCollapsedContent(email)}
                {email.attachments.length ? this.renderAttachments(email.attachments) : undefined}
                {email.trackedViewsCount && email.trackedViewsCount > 0
                    ? this.renderViewCount(email.trackedViewsCount)
                    : undefined}
                {sequenceInfo ? this.renderSequenceInfo(sequenceInfo) : undefined}
                {this.renderHeaderRight(email)}
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {this.props.deletingDialog}
                </div>
            </div>
        );
    }

    renderHeaderLeft(email: EmailListItem, status?: EmailStatus) {
        const allRecipients = this.props.isReceivedEmail
            ? [normalizeEmailAddresseeEntity(email.from)]
            : email.to.map((address) => normalizeEmailAddresseeEntity(address).name);

        return (
            <div>
                <div className={styles['header-left']}>
                    <div
                        data-tip={allRecipients.length > 1 ? allRecipients.join(', ') : undefined}
                        className={styles['to-container']}
                    >
                        {/* @ts-expect-error */}
                        <EmailLinkedEntities
                            emailAddressees={this.props.isReceivedEmail ? [email.from] : email.to}
                            useEntityColorIfUser={true}
                            showAll={false}
                        />
                    </div>
                    {status ? (
                        <div className={styles['status-badge-collapsed']}>
                            <StatusBadge {...status} />
                        </div>
                    ) : undefined}
                </div>
            </div>
        );
    }

    renderCollapsedContent(email: EmailListItem) {
        return (
            <div className={styles['email-contents-container']}>
                <div className={styles['email-contents']}>
                    <span className={styles['email-content-text']}>
                        <span className={styles['subject-container']}>{email.subject}</span>
                        <span className={styles['body-container']}>{email.bodyText}</span>
                    </span>
                </div>
            </div>
        );
    }

    renderViewCount(trackedViews: number) {
        return (
            <div
                data-tip={trackedViews === 1 ? '1 view' : `${trackedViews} views`}
                data-event='mouseenter focus'
                data-event-off='mouseleave blur click'
                className={styles['inline-icon-container']}
            >
                <span className={styles['tracked-view-count']}>{trackedViews}&nbsp;</span>
                <EyeIcon size={15} wrapWithDiv={false} className={styles['inline-icon']} />
            </div>
        );
    }

    renderAttachments(attachments: any[]) {
        return (
            <button
                type='button'
                onClick={(e) => {
                    e.stopPropagation();
                    this.props.onExpandRow();
                }}
                tabIndex={0}
                data-tip={
                    attachments.length === 1
                        ? attachments[0].name
                        : `${attachments.length} attachments`
                }
                data-event='mouseenter focus'
                data-event-off='mouseleave blur click'
                className={styles['inline-icon-container']}
            >
                <HtmlAttachmentIcon
                    size={18}
                    wrapWithDiv={false}
                    className={styles['inline-icon']}
                />
            </button>
        );
    }

    renderSequenceInfo(sequenceInfo: SequenceInfo) {
        return (
            <button
                type='button'
                onClick={(e) => {
                    e.stopPropagation();
                    if (this.props.navToSequenceDetails) {
                        this.props.navToSequenceDetails();
                    }
                }}
                tabIndex={0}
                data-tip={
                    sequenceInfo && sequenceInfo.messageNumber
                        ? `Message ${sequenceInfo.messageNumber} of ${sequenceInfo.messageCount} in ${sequenceInfo.name}`
                        : undefined
                }
                data-event='mouseenter focus'
                data-event-off='mouseleave blur click'
                className={styles['inline-icon-container']}
            >
                <CampaignIcon size={15} wrapWithDiv={false} className={styles['inline-icon']} />
            </button>
        );
    }

    renderHeaderRight(email: EmailListItem) {
        return (
            <div className={styles['header-right']}>
                <div className={styles['timestamp-container']}>
                    <FormattedTimestamp
                        format={FormattedTimestamp.formats.time}
                        datetime={email.timestamp}
                    />
                </div>
                {this.props.actions.length > 0 ? (
                    <ActionMenu getRef={this.props.getRef} options={this.props.actions} />
                ) : undefined}
            </div>
        );
    }
}
